import { init as initFullStory } from "@fullstory/browser";
import { StyledEngineProvider, ThemeProvider, createTheme, responsiveFontSizes } from "@mui/material/styles";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import * as Sentry from "@sentry/react";
import ErrorPage from "_shared/_errors/_views/error-page";
import { SystemNotificationBannerProvider } from "_shared/_providers/banner/system-notification-banner-provider";
import ConfirmDialogProvider from "_shared/_providers/dialogs/confirm/confirm-dialog";
import DialogProvider from "_shared/_providers/dialogs/dialog";
import ImagePreviewDialogProvider from "_shared/_providers/dialogs/image-preview/image-preview-dialog";
import attorneyHubStore from "_shared/_stores/redux-store";
import { ENVIRONMENT, IS_DEV } from "_shared/_utilities/meta";
import Dashboard from "_shared/_views/dashboards/dashboard";
import dashboardLoader from "_shared/_views/dashboards/dashboard-loader";
import invitationLoader from "_shared/_views/loaders/invitation";
import marketplaceLoader from "_shared/_views/loaders/marketplace";
import marketplaceProfileLoader from "_shared/_views/loaders/marketplace-profile";
import marketplaceResultProfileLoader from "_shared/_views/loaders/marketplace-result-profile";
import networkLoader from "_shared/_views/loaders/network";
import rootLoader from "_shared/_views/loaders/root";
import superUserLoader from "_shared/_views/loaders/super-users";
import teamsLoader from "_shared/_views/loaders/teams";
import Main from "_shared/_views/main";
import Calendar from "_shared/dates/_views/calendar";
import Teams from "_shared/teams/teams-page";
import Contracts from "documents/types/contract-documents/_views/contracts-page";
import "./index.css";
import EntityAdministration from "legal-entities/administrators/_views/administration-page";
import entityAdministratorLoader from "legal-entities/administrators/_views/administrator-loader";
import EntityAgreementPage from "legal-entities/agreements/_views/entity-agreement-page";
import AdministratorInvitationPage from "legal-entities/invitations/administrator-invitations/_views/administrator-invitation-page";
import UserEntityMemberInvitationAcceptancePage from "legal-entities/invitations/member-invitations/email-based/_views/user-entity-member-invitation-acceptance-page";
import OpenEntityMemberInvitationPage from "legal-entities/invitations/member-invitations/open/_views/open-entity-member-invitation-acceptance-page";
import EntityOfficerInvitationPage from "legal-entities/invitations/officer-invitations/_views/entity-officer-invitation-page";
import MarketplacePage from "marketplace/_views/marketplace-page";
import ManageProfiles from "marketplace/_views/profiles/profiles-page";
import Bookmarks from "marketplace/bookmarks/_views/bookmarks-page";
import bookmarksLoader from "marketplace/bookmarks/_views/loaders/bookmarks-loader";
import TeamInvitationPage from "marketplace/teams/invitations/_views/team-invitation-page";
import CommunicationsPage from "notifications/_views/communications-page";
import communicationsLoader from "notifications/_views/loader/communications-loader";
import { SnackbarProvider } from "notistack";
import React from "react";
import { createRoot } from "react-dom/client";
import { Provider as ReduxProvider } from "react-redux";
import { createBrowserRouter, Outlet, RouterProvider } from "react-router";

import { RequireSession, SessionProvider } from "users/__session/session-context";
import LoginPage from "users/auth/_views/login-page";
import Network from "users/networking/_views/network-page";
import SuperUserDashboard from "users/super-users/_views/super-user-administration-panel";
import FeeSchedulesPage from "work/fee-schedule-templates/_views/pages/fee-schedules-page";
import { 
  activeFeeScheduleLoader, 
  archivedFeeScheduleLoader, 
  feeScheduleLoader, 
  templateFeeScheduleLoader 
} from "work/fee-schedule-templates/_views/pages/loaders/fee-schedule-loader";
import ActiveProposalPage from "work/proposals/_views/active/active-proposal-page";
import ArchivedProposalPage from "work/proposals/_views/archive/archived-proposal-page";
import DraftProposalPage from "work/proposals/_views/drafts/draft-proposal-page";
import {
  activeProposalLoader,
  archivedProposalLoader,
  draftProposalLoader,
  proposalLoader,
  withDefaultTab,
} from "work/proposals/_views/loaders/proposal-loader";
import ProposalsPage from "work/proposals/_views/proposals-page";
import ViewWorkAgreementPage from "work/work-agreements/_views/view/view-work-agreement-page";
import { 
  activeWorkAgreementLoader, 
  archivedWorkAgreementLoader, 
  workLoader 
} from "work/work-agreements/_views/loaders/work-agreement-loader";
import WorkAgreementsPage from "work/work-agreements/_views/work-agreements-page";
import FeeScheduleTemplatePage from "_shared/fee-schedules/categories/_views/templates/fee-schedule-template-page";

initFullStory({
  orgId: "o-1M2PCX-na1",
  devMode: IS_DEV,
});

Sentry.init({
  dsn: "https://9b4b0f7cb45a485ea5dcef37b8da09d7@o1250895.ingest.sentry.io/6415866",
  enabled: !IS_DEV,
  environment: ENVIRONMENT,
  tracesSampleRate: 1,
});

let theme = createTheme();
theme = responsiveFontSizes(theme);

const RequireSessionLayout = () => (
  <RequireSession>
    <Outlet />
  </RequireSession>
);

const router = createBrowserRouter([
  {
    path: "entity-officer-invitation/:invitationId",
    element: <EntityOfficerInvitationPage />,
    errorElement: <ErrorPage />,
    loader: invitationLoader,
  },
  {
    path: "administrator-invitation/:invitationId",
    element: <AdministratorInvitationPage />,
    errorElement: <ErrorPage />,
    loader: invitationLoader,
  },
  {
    path: "entity-member-invitation/:invitationId",
    element: <OpenEntityMemberInvitationPage />,
    errorElement: <ErrorPage />,
    loader: invitationLoader,
  },
  {
    path: "user-member-invitation/:invitationId",
    element: <UserEntityMemberInvitationAcceptancePage />,
    errorElement: <ErrorPage />,
    loader: invitationLoader,
  },
  {
    path: "login",
    element: <LoginPage />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/",
    element: <Main />,
    errorElement: <ErrorPage />,
    loader: rootLoader,
    children: [
      {
        element: <RequireSessionLayout />,
        errorElement: <ErrorPage />,
        children: [
          {
            path: "dashboard",
            element: <Dashboard />,
            loader: dashboardLoader,
          },

          {
            path: "communications",
            element: <CommunicationsPage />,
            loader: communicationsLoader,
          },
          {
            path: "communications/:pageTab",
            element: <CommunicationsPage />,
            loader: communicationsLoader,
          },
          {
            path: "communications/:pageTab/:action",
            element: <CommunicationsPage />,
            loader: communicationsLoader,
          },
          {
            path: "communications/:pageTab/:action/:id",
            element: <CommunicationsPage />,
            loader: communicationsLoader,
          },

          {
            path: "contracts",
            element: <Contracts />,
          },
          {
            path: "contracts/:pageTab",
            element: <Contracts />,
          },

          {
            path: "work",
            children: [
              {
                index: true,
                element: <WorkAgreementsPage />,
                loader: withDefaultTab("active", workLoader)
              },
              {
                path: ":pageTab",
                element: <WorkAgreementsPage />,
                loader: workLoader
              },

              {
                path: "active/:action/:id",
                element: <ViewWorkAgreementPage />,
                loader: withDefaultTab("details", activeWorkAgreementLoader),
              },
              {
                path: "active/:action/:id/:pageTab",
                element: <ViewWorkAgreementPage />,
                loader: activeWorkAgreementLoader,
              },

              {
                path: "archived/:action/:id",
                element: <ViewWorkAgreementPage />,
                loader: withDefaultTab("details", archivedWorkAgreementLoader),
              },
              {
                path: "archived/:action/:id/:pageTab",
                element: <ViewWorkAgreementPage />,
                loader: archivedWorkAgreementLoader,
              },
            ],
          },

          {
            path: "proposals",
            children: [
              {
                index: true,
                element: <ProposalsPage />,
                loader: withDefaultTab("active", proposalLoader)
              },
              {
                path: ":pageTab",
                element: <ProposalsPage />,
                loader: proposalLoader
              },

              {
                path: "drafts/create",
                element: <DraftProposalPage />,
                loader: withDefaultTab("details", draftProposalLoader),
              },
              {
                path: "drafts/create/:pageTab",
                element: <DraftProposalPage />,
                loader: draftProposalLoader,
              },

              {
                path: "drafts/:action/:id",
                element: <DraftProposalPage />,
                loader: withDefaultTab("details", draftProposalLoader),
              },
              {
                path: "drafts/:action/:id/:pageTab",
                element: <DraftProposalPage />,
                loader: draftProposalLoader,
              },

              {
                path: "active/:action/:id",
                element: <ActiveProposalPage />,
                loader: withDefaultTab("details", activeProposalLoader),
              },
              {
                path: "active/:action/:id/:pageTab",
                element: <ActiveProposalPage />,
                loader: activeProposalLoader,
              },

              {
                path: "archived/:action/:id",
                element: <ArchivedProposalPage />,
                loader: withDefaultTab("details", archivedProposalLoader),
              },
              {
                path: "archived/:action/:id/:pageTab",
                element: <ArchivedProposalPage />,
                loader: archivedProposalLoader,
              },
            ],
          },

          {
            path: "teams",
            element: <Teams />,
            loader: teamsLoader,
          },
          {
            path: "teams/:pageTab",
            element: <Teams />,
            loader: teamsLoader,
          },
          {
            path: "teams/:pageTab/:action",
            element: <Teams />,
            loader: teamsLoader,
          },
          {
            path: "teams/:pageTab/:action/:id",
            element: <Teams />,
            loader: teamsLoader,
          },

          {
            path: "fee-schedules",
            children: [
              {
                index: true,
                element: <FeeSchedulesPage />,
                loader: withDefaultTab("templates", feeScheduleLoader)
              },
              {
                path: ":pageTab",
                element: <FeeSchedulesPage />,
                loader: feeScheduleLoader
              },
              {
                path: "templates/create",
                element: <FeeScheduleTemplatePage />,
                loader: templateFeeScheduleLoader
              },
              {
                path: "templates/:action/:id",
                element: <FeeScheduleTemplatePage />,
                loader: templateFeeScheduleLoader
              },
              {
                path: "active/:action/:id",
                element: <FeeScheduleTemplatePage />,
                loader: activeFeeScheduleLoader
              },
              {
                path: "archived/:action/:id",
                element: <FeeScheduleTemplatePage />,
                loader: archivedFeeScheduleLoader
              },
            ],
          },

          {
            path: "marketplace/vendor-team-invitation/:invitationId",
            element: <TeamInvitationPage />,
          },

          {
            path: "marketplace/profiles",
            element: <ManageProfiles />,
            loader: marketplaceProfileLoader,
          },
          {
            path: "marketplace/profiles/:pageTab",
            element: <ManageProfiles />,
            loader: marketplaceProfileLoader,
          },
          {
            path: "marketplace/profiles/:pageTab/:action",
            element: <ManageProfiles />,
            loader: marketplaceProfileLoader,
          },
          {
            path: "marketplace/profiles/:pageTab/:action/:id",
            element: <ManageProfiles />,
            loader: marketplaceProfileLoader,
          },

          {
            path: "marketplace/bookmarks",
            element: <Bookmarks />,
            loader: bookmarksLoader,
          },
          {
            path: "marketplace/bookmarks/:pageTab",
            element: <Bookmarks />,
            loader: bookmarksLoader,
          },

          {
            path: "marketplace",
            element: <MarketplacePage />,
            loader: marketplaceLoader,
          },
          {
            path: "marketplace/:pageTab",
            element: <MarketplacePage />,
            loader: marketplaceResultProfileLoader,
          },
          {
            path: "marketplace/:pageTab/:id",
            element: <MarketplacePage />,
            loader: marketplaceResultProfileLoader,
          },

          {
            path: "network",
            element: <Network />,
            loader: networkLoader,
          },
          {
            path: "network/:pageTab",
            element: <Network />,
            loader: networkLoader,
          },
          {
            path: "network/:pageTab/:action",
            element: <Network />,
            loader: networkLoader,
          },
          {
            path: "network/:pageTab/:action/:id",
            element: <Network />,
            loader: networkLoader,
          },

          {
            path: "calendar",
            element: <Calendar />,
          },

          {
            path: "administration",
            element: <EntityAdministration />,
            loader: entityAdministratorLoader,
          },
          {
            path: "administration/:pageTab",
            element: <EntityAdministration />,
            loader: entityAdministratorLoader,
          },
          {
            path: "administration/:pageTab/:action",
            element: <EntityAdministration />,
            loader: entityAdministratorLoader,
          },
          {
            path: "administration/:pageTab/:action/:id",
            element: <EntityAdministration />,
            loader: entityAdministratorLoader,
          },

          {
            path: "super-user",
            element: <SuperUserDashboard />,
            loader: superUserLoader,
          },
          {
            path: "super-user/:pageTab",
            element: <SuperUserDashboard />,
            loader: superUserLoader,
          },
          {
            path: "super-user/:pageTab/:profileType/:id",
            element: <SuperUserDashboard />,
            loader: superUserLoader,
          },

          {
            path: "entity-agreements/:id",
            element: <EntityAgreementPage />,
          },
        ],
      },
    ],
  },
  {
    path: "*",
    element: <ErrorPage error={{ status: 404, statusText: "Page Not Found" }} />,
  },
]);

const container = document.getElementById("root");
if (!container) {
  throw new Error("Root element not found");
}

const root = createRoot(container);
root.render(
  <React.StrictMode>
    <ReduxProvider store={attorneyHubStore}>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <SystemNotificationBannerProvider>
              <SnackbarProvider maxSnack={3}>
                <SessionProvider>
                  <ConfirmDialogProvider>
                    <ImagePreviewDialogProvider>
                      <DialogProvider>
                        <RouterProvider router={router} />
                      </DialogProvider>
                    </ImagePreviewDialogProvider>
                  </ConfirmDialogProvider>
                </SessionProvider>
              </SnackbarProvider>
            </SystemNotificationBannerProvider>
          </LocalizationProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    </ReduxProvider>
  </React.StrictMode>,
);
